import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import { isMobile } from 'react-device-detect';

const useStyles = makeStyles((theme) => ({
  ShortcutpageContainer: {
    fontFamily: "'Inter', sans-serif",
    textAlign: 'center',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    gap: '20px',
    '@media (orientation: landscape) and (max-height: 600px)': {
      padding: '10px',
      gap: '10px',
    },
  },
  headerText: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: '20px',
    textShadow: '0px 0px 10px rgba(77, 121, 255, 0.8)',
  },

  buttonsContainer: {
    display: 'grid',
    gap: '20px',
    margin: '0 auto',
    width: '100%',
    maxWidth: '1200px',
    flex: 1,
    alignContent: 'start',
    
    // Desktop default: 4 columns
    gridTemplateColumns: 'repeat(4, minmax(200px, 1fr))',
    
    // Landscape mode: maintain 4 columns but adjust sizes
    '@media (orientation: landscape)': {
      gridTemplateColumns: 'repeat(4, minmax(150px, 1fr))',
      gap: '15px',
      maxWidth: '1000px',
    },
    
    // Tablet portrait: 2 columns
    '@media (max-width: 768px) and (orientation: portrait)': {
      gridTemplateColumns: 'repeat(2, minmax(150px, 1fr))',
      gap: '15px',
    },
    
    // Mobile: maintain 2 columns
    '@media (max-width: 480px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '10px',
    },
  },
  ShortcutpageButton: {
    backgroundColor: '#1e1e1e',
    color: '#ffffff',
    padding: '10px 15px',
    fontSize: '1.05rem',
    borderRadius: '25px',
    border: '1px solid #4d79ff',
    fontWeight: '700',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    cursor: 'pointer',
    position: 'relative',
    textTransform: 'none',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease',
    height: '75px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
      backgroundColor: '#292929',
    },
    
    // Adjust button height for different screen sizes
    '@media (orientation: landscape) and (max-height: 600px)': {
      height: '50px',
      fontSize: '0.95rem',
      padding: '5px 10px',
    },
    
    '@media (max-width: 768px)': {
      height: '70px',
      fontSize: '0.95rem',
    },
    
    '@media (max-width: 480px)': {
      height: '60px',
      fontSize: '0.9rem',
      padding: '8px 12px',
    },
  },  

  newBadge: {
    position: 'absolute',
    top: '-10px',
    right: '-10px',
    backgroundColor: '#ff6f00',
    color: '#fff',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
  },
}));

const ShortcutPage = ({ setShowHomePage, setShowShortcutPage, setShowE3DMarketplace, setShowSwapDialog, setShowStakeDialog }) => {
  console.log('ShortcutPage props check:', { 
    hasSetShowSwapDialog: typeof setShowSwapDialog === 'function', 
    hasSetShowStakeDialog: typeof setShowStakeDialog === 'function'
  });
  
  const classes = useStyles();

  const navigate = useNavigate();
  const location = useLocation();

  const goTo = (path, specialAction) => {
    // Check for special paths that should open as dialogs
    if (specialAction) {
      console.log('ShortcutPage: Executing special action instead of navigating');
      // Execute the specialAction directly - don't rely on closure
      if (path === '/swap' && typeof setShowSwapDialog === 'function') {
        console.log('ShortcutPage: Directly calling setShowSwapDialog(true)');
        // Use the App component's debugSetShowSwapDialog via props
        setShowSwapDialog(true);
        // Important: prevent navigation to the swap page - stay on current page
        return;
      } else if (path === '/stake' && typeof setShowStakeDialog === 'function') {
        console.log('ShortcutPage: Directly calling setShowStakeDialog(true)');
        // Use the App component's debugSetShowStakeDialog via props
        setShowStakeDialog(true);
        // Important: prevent navigation to the stake page - stay on current page
        return;
      } else {
        // Fall back to the provided specialAction
        specialAction();
        return;
      }
    }
    setShowShortcutPage(false);
    navigate(path, {
      state: { from: location.pathname }
    });
  };

  // Special direct handlers for dialog buttons that use global window functions
  const handleOpenSwapDialog = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('ShortcutPage: Using global function to open Swap dialog');
    
    // Use the global function instead of props
    if (typeof window.openE3DSwapDialog === 'function') {
      window.openE3DSwapDialog();
    } else {
      console.error('ShortcutPage: Global openE3DSwapDialog function not found!');
    }
    
    return false;
  };

  const handleOpenStakeDialog = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('ShortcutPage: Using global function to open Stake dialog');
    
    // Use the global function instead of props
    if (typeof window.openE3DStakeDialog === 'function') {
      window.openE3DStakeDialog();
    } else {
      console.error('ShortcutPage: Global openE3DStakeDialog function not found!');
    }
    
    return false;
  };

  const buttons = [
    { label: 'E3D Token Graph', path: '/token', tooltip: 'View the E3D token relationship graph' },
    { label: 'E3D User Graph', path: '/token?organic=true&users=true', tooltip: 'Explore user interactions with E3D tokens' },
    { label: 'E3D NFT Graph', path: '/token?nft=true', tooltip: 'View NFT relationships in the E3D ecosystem' },
    { label: 'Token Price List', path: '/price', tooltip: 'Check current token prices and stats' },
    { 
      label: 'E3D Token Swap', 
      path: '/swap', 
      tooltip: 'Swap ETH for E3D tokens directly', 
      isNew: true,
      directHandler: handleOpenSwapDialog
    },
    { 
      label: 'E3D Staking', 
      path: '/stake', 
      tooltip: 'Stake your E3D tokens for rewards', 
      isNew: true,
      directHandler: handleOpenStakeDialog
    },
    { label: 'New Tokens', path: '/token?new=true', isNew: true, tooltip: 'Discover newly added tokens' },
    { label: 'New NFTs', path: '/token?new=true&nft=true', isNew: true, tooltip: 'Browse recently added NFTs' },
    { label: 'E3D NFT Marketplace', path: '/marketplace', tooltip: 'Buy and sell NFTs in the marketplace' },
    { label: 'NFT Explorer', path: '/nft', tooltip: 'Search and explore NFT collections' },
    { label: 'AI Newsletter', path: '/news', tooltip: 'Read AI-generated crypto news' },
    ...(!isMobile ? [{ label: 'Open PCAP', path: '/pcap', tooltip: 'Open and analyze packet capture files' }] : []),
  ];

  return (
    <div className={classes.ShortcutpageContainer}>
      <div className={classes.buttonsContainer}>
        {buttons.map(({ label, path, isNew, tooltip, specialAction, directHandler }) => (
          <Tooltip key={label} title={tooltip} arrow placement="top">
            <button
              className={classes.ShortcutpageButton}
              onClick={directHandler || (() => goTo(path, specialAction))}
            >
              {label}
              {isNew && <div className={classes.newBadge}>NEW</div>}
            </button>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default ShortcutPage;
